'use client';

import { useMemo } from 'react';
import { getCities, getCountries } from '@/lib/data-source';
import { getCitySlugForId, uiFb } from '@/lib/i18n';
import { getCityLogoUrl } from '@/lib/images';
import { NavDropdown, NavDropdownItem } from './NavDropdown';
import { EntityLogo } from '@/components/ui/EntityLogo';
import { Flag } from '@/components/ui/Flag';

interface NavCityLinksProps {
  currentLang: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  activeCityId?: string;
}

export function NavCityLinks({ currentLang, isOpen, onToggle, onClose, activeCityId }: NavCityLinksProps) {
  const allCities = useMemo(() => getCities(), []);
  const activeCity = activeCityId ? allCities.find(c => c.id === activeCityId) : undefined;

  const grouped = useMemo(() => {
    const countries = getCountries();
    const countryMap = new Map(countries.map(c => [c.code, c]));

    const byCountry: Record<string, { code: string; name: string; flag: string; cities: typeof allCities }> = {};
    for (const city of allCities) {
      if (!byCountry[city.country]) {
        const country = countryMap.get(city.country);
        byCountry[city.country] = {
          code: city.country,
          name: country?.name ?? city.country,
          flag: country?.flag ?? '',
          cities: [],
        };
      }
      byCountry[city.country].cities.push(city);
    }
    return Object.values(byCountry);
  }, [allCities]);

  return (
    <NavDropdown
      isOpen={isOpen}
      onToggle={onToggle}
      onClose={onClose}
      align="right"
      label={uiFb('ui.select_city', 'Select City', currentLang)}
      trigger={
        <span className="flex items-center gap-1.5">
          {activeCity ? (
            <>
              <span className="text-sm">{activeCity.icon}</span>
              <span>{activeCity.name}</span>
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
              <span>{uiFb('ui.select_city', 'Select City', currentLang)}</span>
            </>
          )}
        </span>
      }
    >
      {grouped.map(group => (
        <div key={group.name}>
          <div className="px-3 pb-1 pt-2.5 text-caption font-semibold uppercase tracking-wider text-text-tertiary">
            <Flag code={group.code} size={14} /> {group.name}
          </div>
          {group.cities.map(city => {
            const citySlug = getCitySlugForId(city.id, currentLang);
            return (
              <NavDropdownItem
                key={city.id}
                icon={<EntityLogo src={getCityLogoUrl(city)} alt={city.name} size={18} fallbackEmoji={city.icon} />}
                label={city.name}
                href={citySlug ? `/${citySlug}` : '#'}
                onClick={onClose}
                isActive={city.id === activeCityId}
              />
            );
          })}
        </div>
      ))}
    </NavDropdown>
  );
}
